import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query"
import { useApi } from "./api"
import { useMemo } from "react"

export enum BankAccountOwnerType {
  INDIVIDUAL = "INDIVIDUAL",
  BUSINESS = "BUSINESS",
}

export enum BankAccountType {
  US = "US",
  EU = "EU",
}

export interface BankAccount {
  id: string;
  type: BankAccountType;
  bankName: string;
  firstName?: string;
  lastName?: string;
  accountOwnerName: string;
  details: {
    accountNumber?: string;
    routingNumber?: string;
    iban?: string;
    bic?: string;
  };
  accountOwnerType: BankAccountOwnerType;
  userAddressId: string;
}

export interface BankAccountGet extends BankAccount {
  createdAt: string;
}

export function createEmptyBankAccount(): BankAccount {
  return {
    id: "",
    type: BankAccountType.US,
    bankName: "",
    firstName: "",
    lastName: "",
    accountOwnerName: "",
    details: {
      accountNumber: "",
      routingNumber: "",
    },
    accountOwnerType: BankAccountOwnerType.INDIVIDUAL,
    userAddressId: "",
  }
}

export function useBankAccounts(pageSize = 10) {
  const { get } = useApi()

  const query = useInfiniteQuery({
    queryKey: ["bank-accounts"],
    queryFn: ({ pageParam = 0 }) => get(`users/me/bankAccounts?skip=${pageParam}&take=10`),
    initialPageParam: 0,
    getNextPageParam: (lastPage: BankAccountGet[], allPages) => {
      if (lastPage.length < pageSize) return undefined
      return allPages.length * pageSize
    },
  })

  return {
    bankAccounts: query.data?.pages.flat(),
    hasMore: query.hasNextPage,
    isLoading: query.isLoading,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    // Backward compatibility
    size: query.data?.pages.length || 0,
    setSize: (size: number) => {
      if (size > (query.data?.pages.length || 0)) {
        query.fetchNextPage()
      }
    },
    mutate: query.refetch,
  }
}

export function useBankAccount(id: string) {
  const { get } = useApi()

  const query = useQuery({
    queryKey: ["bank-account", id],
    queryFn: () => get(`users/me/bankAccounts/${id}`),
    enabled: !!id,
  })

  return {
    customer: query.data,
    isLoading: query.isLoading,
    error: query.error,
    mutate: query.refetch,
  }
}

export function useBankAccountsActions() {
  const { remove, post, put, get } = useApi()
  const queryClient = useQueryClient()

  return useMemo(
    () => {
      const updateList = () => queryClient.invalidateQueries({ queryKey: ["bank-accounts"] })

      return {
        remove: async (id: string) => {
          await remove(`users/me/bankAccounts/${id}`)
          updateList()
        },
        create: async (
          bankAccount: Partial<BankAccount>,
        ): Promise<BankAccount> => {
          const createdBankAccount = await post(
            `users/me/bankAccounts`,
            bankAccount,
          )
          updateList()
          return createdBankAccount
        },
        update: async (id: string, bankAccount: Partial<BankAccount>) => {
          const updatedBankAccount = await put(
            `users/me/bankAccounts/${id}`,
            bankAccount,
          )
          queryClient.invalidateQueries({ queryKey: ["bank-account", id] })
          return updatedBankAccount
        },
        get: async (id: string): Promise<BankAccount> => {
          return get(`users/me/bankAccounts/${id}`)
        },
      }
    },
    [remove, post, put, get, queryClient],
  )
}

export function useLaunchBankAccountLink() {
  const { post, patch } = useApi()

  return async function launchBankAccountLink() {
    const { link_token } = await post("users/me/bankAccounts/linkTokens", {})

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = (window as any).Plaid.create({
      link_token,
      onSuccess: async (publicToken: string) => {
        await patch(`users/me/bankAccounts/linkTokens/${link_token}`, {
          publicToken,
        })
      },
    })
    handler.open()
  }
}

export function getAccountNumber(bankAccount: BankAccount) {
  const number = String(
    bankAccount.type === BankAccountType.US
      ? bankAccount.details.accountNumber
      : bankAccount.details.iban,
  )
  return "****" + number.slice(-4)
}
